import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { BarChart3, TrendingUp, DollarSign, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { dealService, type Deal } from '@/services/deal.service';

const PIE_COLORS = ['#22C55E', '#EF4444', '#F59E0B', '#6B7280'];

export function DealAnalyticsPage() {
  const [selectedDealId, setSelectedDealId] = useState<string>('');

  // Fetch all deals (active + history) for the selector
  const { data: activeDeals = [] } = useQuery({
    queryKey: ['active-deals'],
    queryFn: dealService.getActiveDeals,
  });

  const { data: historyDeals = [] } = useQuery({
    queryKey: ['deal-history'],
    queryFn: dealService.getDealHistory,
  });

  const allDeals = [...activeDeals, ...historyDeals];

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['deal-analytics', selectedDealId],
    queryFn: () => dealService.getDealAnalytics(selectedDealId),
    enabled: !!selectedDealId,
  });

  const dealOptions = [
    { value: '', label: 'Select a deal to view analytics' },
    ...allDeals.map((deal: Deal) => ({
      value: deal.id,
      label: `${deal.item.name} (${deal.status})`,
    })),
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="h-8 w-8" />
          Deal Analytics
        </h1>
        <p className="mt-1 text-muted-foreground">
          View detailed performance metrics for each deal
        </p>
      </div>

      {/* Deal Selector */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <Select
            label="Select a Deal"
            options={dealOptions}
            value={selectedDealId}
            onChange={(e) => setSelectedDealId(e.target.value)}
          />
        </CardContent>
      </Card>

      {!selectedDealId ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="mb-4 h-12 w-12 text-gray-300" />
            <p className="text-lg text-muted-foreground">Select a deal to view its analytics</p>
          </CardContent>
        </Card>
      ) : analyticsLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Spinner />
        </div>
      ) : analytics ? (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-blue-100 p-2">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Claims</p>
                    <p className="text-2xl font-bold">{analytics.totalClaims}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-green-100 p-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                    <p className="text-2xl font-bold">
                      {(analytics.conversionRate * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-orange-100 p-2">
                    <DollarSign className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                    <p className="text-2xl font-bold">{analytics.totalRevenue.toFixed(2)} AZN</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Claims Over Time */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Claims Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.claimsOverTime.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.claimsOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" fontSize={12} />
                      <YAxis allowDecimals={false} fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-64 items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Claim Status Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Claim Status Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics.claimsByStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={analytics.claimsByStatus}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ status, count }) => `${status}: ${count}`}
                      >
                        {analytics.claimsByStatus.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={PIE_COLORS[index % PIE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-64 items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}
    </div>
  );
}
